/**
 * MySubmissionsPage
 *
 * Displays the customer's form submission history.
 * Shows submission date, form name (via ID), and submitted data summary.
 *
 * Requirements: Task 12 - Forms Pages
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { FileText, RefreshCw, ChevronLeft, ChevronRight, Calendar } from '@bloomcode/icons';

import { useMyFormSubmissions } from '@/hooks/useForms';

/**
 * Formats a date string to a readable format
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Unknown date';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Summarizes submission data for display
 */
function summarizeData(data: unknown): string {
  if (!data) return 'No data';
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    if (typeof parsed === 'object' && parsed !== null) {
      const entries = Object.entries(parsed as Record<string, unknown>);
      if (entries.length === 0) return 'Empty submission';
      const summary = entries
        .slice(0, 3)
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join(', ');
      return entries.length > 3 ? `${summary}...` : summary;
    }
    return String(parsed);
  } catch {
    return 'Data available';
  }
}

/**
 * Empty state when no submissions exist
 */
function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Submissions Yet</h3>
        <p className="text-muted-foreground max-w-sm">
          When you submit forms, your submission history will appear here.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function MySubmissionsPage() {
  const [page, setPage] = useState(1);

  const { submissions, pagination, totalCount, isLoading, error, refetch } = useMyFormSubmissions({
    page,
    limit: 10,
  });

  // Loading state
  if (isLoading && submissions.length === 0) {
    return (
      <>
        <Helmet>
          <title>My Submissions | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">My Submissions</h1>
            <p className="text-muted-foreground">View your past form submissions.</p>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 w-32 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-24 rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error && submissions.length === 0) {
    return (
      <>
        <Helmet>
          <title>My Submissions | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">My Submissions</h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load submissions</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Submissions | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/forms">Forms</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>My Submissions</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">My Submissions</h1>
            <p className="text-muted-foreground">
              {totalCount > 0
                ? `${totalCount} submission${totalCount !== 1 ? 's' : ''}`
                : 'View your past form submissions.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            aria-label="Refresh submissions"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Submissions List or Empty State */}
        {submissions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="space-y-3" role="list" aria-label="Submissions list">
              {submissions.map((submission) => (
                <Card key={submission.id} role="listitem">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-foreground text-sm">
                            Form: {submission.formId}
                          </h3>
                          {submission.source && (
                            <Badge variant="outline" className="text-xs">
                              {submission.source}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {summarizeData(submission.data)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(submission.createdAt)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <PaginationControls
                page={pagination.page}
                totalPages={pagination.pages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
